import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateJST } from '../../utils/dateUtils';

interface EvaluationPeriod {
  period_id: string;
  period_name: string;
  goal_setting_start?: string;
  goal_setting_end?: string;
  mid_term_review_start?: string;
  mid_term_review_end?: string;
  self_eval_start: string;
  self_eval_end: string;
  manager_eval_start: string;
  manager_eval_end: string;
  dept_eval_start: string;
  dept_eval_end: string;
  hr_review_start: string;
  hr_review_end: string;
  company_adjustment_rate: number;
  target_average_increase_rate: number;
  is_adjustment_rate_locked: boolean;
  status: 'draft' | 'root_defined' | 'active' | 'closed';
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface PeriodFormData {
  period_id: string;
  period_name: string;
  goal_setting_start: string;
  goal_setting_end: string;
  mid_term_review_start: string;
  mid_term_review_end: string;
  self_eval_start: string;
  self_eval_end: string;
  manager_eval_start: string;
  manager_eval_end: string;
  dept_eval_start: string;
  dept_eval_end: string;
  hr_review_start: string;
  hr_review_end: string;
  company_adjustment_rate: number;
  target_average_increase_rate: number;
  status: 'draft' | 'root_defined' | 'active' | 'closed';
}

const EvaluationPeriodManagementPage: React.FC = () => {
  const [periods, setPeriods] = useState<EvaluationPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<EvaluationPeriod | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<EvaluationPeriod | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [formData, setFormData] = useState<PeriodFormData>({
    period_id: '',
    period_name: '',
    goal_setting_start: '',
    goal_setting_end: '',
    mid_term_review_start: '',
    mid_term_review_end: '',
    self_eval_start: '',
    self_eval_end: '',
    manager_eval_start: '',
    manager_eval_end: '',
    dept_eval_start: '',
    dept_eval_end: '',
    hr_review_start: '',
    hr_review_end: '',
    company_adjustment_rate: 0,
    target_average_increase_rate: 0,
    status: 'draft'
  });

  useEffect(() => {
    fetchPeriods();
  }, [statusFilter]);

  const fetchPeriods = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};

      const response = await api.get('/evaluation-periods', {
        params
      });

      if (response.data.success) {
        setPeriods(response.data.data || []);
      }
    } catch (err) {
      console.error('評価期間データ取得エラー:', err);
      const errorMsg = extractErrorMessage(err, '評価期間データの取得に失敗しました');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setEditMode(false);
    setSelectedPeriod(null);
    setFormData({
      period_id: '',
      period_name: '',
      goal_setting_start: '',
      goal_setting_end: '',
      mid_term_review_start: '',
      mid_term_review_end: '',
      self_eval_start: '',
      self_eval_end: '',
      manager_eval_start: '',
      manager_eval_end: '',
      dept_eval_start: '',
      dept_eval_end: '',
      hr_review_start: '',
      hr_review_end: '',
      company_adjustment_rate: 0,
      target_average_increase_rate: 0,
      status: 'draft'
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (period: EvaluationPeriod) => {
    setEditMode(true);
    setSelectedPeriod(period);
    setFormData({
      period_id: period.period_id,
      period_name: period.period_name,
      goal_setting_start: period.goal_setting_start ? period.goal_setting_start.split('T')[0] : '',
      goal_setting_end: period.goal_setting_end ? period.goal_setting_end.split('T')[0] : '',
      mid_term_review_start: period.mid_term_review_start ? period.mid_term_review_start.split('T')[0] : '',
      mid_term_review_end: period.mid_term_review_end ? period.mid_term_review_end.split('T')[0] : '',
      self_eval_start: period.self_eval_start.split('T')[0],
      self_eval_end: period.self_eval_end.split('T')[0],
      manager_eval_start: period.manager_eval_start.split('T')[0],
      manager_eval_end: period.manager_eval_end.split('T')[0],
      dept_eval_start: period.dept_eval_start.split('T')[0],
      dept_eval_end: period.dept_eval_end.split('T')[0],
      hr_review_start: period.hr_review_start.split('T')[0],
      hr_review_end: period.hr_review_end.split('T')[0],
      company_adjustment_rate: period.company_adjustment_rate,
      target_average_increase_rate: period.target_average_increase_rate || 0,
      status: period.status
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedPeriod(null);
  };

  const handleFormChange = (field: keyof PeriodFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.period_id.trim()) {
      return '期間IDを入力してください';
    }
    if (!formData.period_name.trim()) {
      return '期間名を入力してください';
    }

    const goalStartRaw = formData.goal_setting_start.trim();
    const goalEndRaw = formData.goal_setting_end.trim();
    if ((goalStartRaw && !goalEndRaw) || (!goalStartRaw && goalEndRaw)) {
      return '目標設定の開始日と終了日は両方入力するか、両方未入力にしてください';
    }

    const midStartRaw = formData.mid_term_review_start.trim();
    const midEndRaw = formData.mid_term_review_end.trim();
    if ((midStartRaw && !midEndRaw) || (!midStartRaw && midEndRaw)) {
      return '中間レビューの開始日と終了日は両方入力するか、両方未入力にしてください';
    }

    // 日付の整合性チェック
    if (goalStartRaw && goalEndRaw) {
      const goalStart = new Date(goalStartRaw);
      const goalEnd = new Date(goalEndRaw);
      if (goalStart > goalEnd) {
        return '目標設定の開始日は終了日より前である必要があります';
      }
    }

    if (midStartRaw && midEndRaw) {
      const midStart = new Date(midStartRaw);
      const midEnd = new Date(midEndRaw);
      if (midStart > midEnd) {
        return '中間レビューの開始日は終了日より前である必要があります';
      }
    }

    const selfStart = new Date(formData.self_eval_start);
    const selfEnd = new Date(formData.self_eval_end);
    const mgrStart = new Date(formData.manager_eval_start);
    const mgrEnd = new Date(formData.manager_eval_end);
    const deptStart = new Date(formData.dept_eval_start);
    const deptEnd = new Date(formData.dept_eval_end);
    const hrStart = new Date(formData.hr_review_start);
    const hrEnd = new Date(formData.hr_review_end);

    if (selfStart > selfEnd) {
      return '自己評価の開始日は終了日より前である必要があります';
    }
    if (mgrStart > mgrEnd) {
      return '上長評価の開始日は終了日より前である必要があります';
    }
    if (deptStart > deptEnd) {
      return '部門評価の開始日は終了日より前である必要があります';
    }
    if (hrStart > hrEnd) {
      return 'HR確認の開始日は終了日より前である必要があります';
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      if (editMode && selectedPeriod) {
        // 更新
        const response = await api.put(
          `/evaluation-periods/${selectedPeriod.period_id}`,
          formData
        );

        if (response.data.success) {
          toast.success('評価期間を更新しました');
          fetchPeriods();
          handleCloseDialog();
        }
      } else {
        // 新規作成
        const response = await api.post(
          '/evaluation-periods',
          formData
        );

        if (response.data.success) {
          toast.success('評価期間を作成しました');
          fetchPeriods();
          handleCloseDialog();
        }
      }
    } catch (err) {
      console.error('評価期間の保存エラー:', err);
      toast.error(extractErrorMessage(err, '評価期間の保存に失敗しました'));
    }
  };

  const handleOpenDeleteConfirm = (period: EvaluationPeriod) => {
    setPeriodToDelete(period);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setPeriodToDelete(null);
  };

  const handleDelete = async () => {
    if (!periodToDelete) return;

    try {
      const response = await api.delete(
        `/evaluation-periods/${periodToDelete.period_id}`
      );

      if (response.data.success) {
        toast.success('評価期間を削除しました');
        fetchPeriods();
        handleCloseDeleteConfirm();
      }
    } catch (err) {
      console.error('評価期間の削除エラー:', err);
      toast.error(extractErrorMessage(err, '評価期間の削除に失敗しました'));
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return '下書き';
      case 'root_defined':
        return 'ルート定義済';
      case 'active':
        return '有効';
      case 'closed':
        return '終了';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'root_defined':
        return 'info';
      case 'active':
        return 'success';
      case 'closed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">評価期間管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={statusFilter}
              label="ステータス"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">すべて</MenuItem>
              <MenuItem value="draft">下書き</MenuItem>
              <MenuItem value="root_defined">ルート定義済</MenuItem>
              <MenuItem value="active">有効</MenuItem>
              <MenuItem value="closed">終了</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPeriods}
          >
            更新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            新規作成
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>期間ID</TableCell>
                  <TableCell>期間名</TableCell>
                  <TableCell>自己評価期間</TableCell>
                  <TableCell>上長評価期間</TableCell>
                  <TableCell>部門評価期間</TableCell>
                  <TableCell>HR確認期間</TableCell>
                  <TableCell>目標平均昇給率</TableCell>
                  <TableCell>会社調整率</TableCell>
                  <TableCell>ロック状態</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      読み込み中...
                    </TableCell>
                  </TableRow>
                ) : periods.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      評価期間がありません
                    </TableCell>
                  </TableRow>
                ) : (
                  periods.map((period) => (
                    <TableRow key={period.period_id}>
                      <TableCell>{period.period_id}</TableCell>
                      <TableCell>{period.period_name}</TableCell>
                      <TableCell>
                        {formatDateJST(period.self_eval_start)} ～ {formatDateJST(period.self_eval_end)}
                      </TableCell>
                      <TableCell>
                        {formatDateJST(period.manager_eval_start)} ～ {formatDateJST(period.manager_eval_end)}
                      </TableCell>
                      <TableCell>
                        {formatDateJST(period.dept_eval_start)} ～ {formatDateJST(period.dept_eval_end)}
                      </TableCell>
                      <TableCell>
                        {formatDateJST(period.hr_review_start)} ～ {formatDateJST(period.hr_review_end)}
                      </TableCell>
                      <TableCell>{period.target_average_increase_rate || 0}%</TableCell>
                      <TableCell>{period.company_adjustment_rate || 0}%</TableCell>
                      <TableCell>
                        <Chip
                          label={period.is_adjustment_rate_locked ? '確定済' : '未確定'}
                          color={period.is_adjustment_rate_locked ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(period.status)}
                          color={getStatusColor(period.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="編集">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(period)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteConfirm(period)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 作成・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? '評価期間を編集' : '新規評価期間を作成'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="期間ID"
                value={formData.period_id}
                onChange={(e) => handleFormChange('period_id', e.target.value)}
                disabled={editMode}
                placeholder="例: 2025_total"
                helperText="一意の期間IDを入力してください"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="期間名"
                value={formData.period_name}
                onChange={(e) => handleFormChange('period_name', e.target.value)}
                placeholder="例: 2025年評価"
                helperText="表示用の期間名を入力してください"
              />
            </Grid>

            {/* 目標設定期間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                目標設定期間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="目標設定開始日"
                type="date"
                value={formData.goal_setting_start}
                onChange={(e) => handleFormChange('goal_setting_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="目標設定終了日"
                type="date"
                value={formData.goal_setting_end}
                onChange={(e) => handleFormChange('goal_setting_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 中間レビュー期間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                中間レビュー期間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="中間レビュー開始日"
                type="date"
                value={formData.mid_term_review_start}
                onChange={(e) => handleFormChange('mid_term_review_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="中間レビュー終了日"
                type="date"
                value={formData.mid_term_review_end}
                onChange={(e) => handleFormChange('mid_term_review_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 自己評価期間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                自己評価期間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="自己評価開始日"
                type="date"
                value={formData.self_eval_start}
                onChange={(e) => handleFormChange('self_eval_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="自己評価終了日"
                type="date"
                value={formData.self_eval_end}
                onChange={(e) => handleFormChange('self_eval_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 上長評価期間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                上長評価期間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="上長評価開始日"
                type="date"
                value={formData.manager_eval_start}
                onChange={(e) => handleFormChange('manager_eval_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="上長評価終了日"
                type="date"
                value={formData.manager_eval_end}
                onChange={(e) => handleFormChange('manager_eval_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 部門評価期間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                部門評価期間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="部門評価開始日"
                type="date"
                value={formData.dept_eval_start}
                onChange={(e) => handleFormChange('dept_eval_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="部門評価終了日"
                type="date"
                value={formData.dept_eval_end}
                onChange={(e) => handleFormChange('dept_eval_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* HR確認期間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                HR確認期間
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HR確認開始日"
                type="date"
                value={formData.hr_review_start}
                onChange={(e) => handleFormChange('hr_review_start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HR確認終了日"
                type="date"
                value={formData.hr_review_end}
                onChange={(e) => handleFormChange('hr_review_end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 目標平均昇給率と会社調整率 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                昇給率設定
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="目標平均昇給率"
                type="number"
                value={formData.target_average_increase_rate}
                onChange={(e) => handleFormChange('target_average_increase_rate', parseFloat(e.target.value) || 0)}
                disabled={selectedPeriod?.is_adjustment_rate_locked}
                InputProps={{
                  endAdornment: '%'
                }}
                helperText="全従業員の平均昇給率の目標値を設定してください"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="会社調整率（R）"
                type="number"
                value={formData.company_adjustment_rate}
                disabled
                InputProps={{
                  endAdornment: '%',
                  readOnly: true
                }}
                helperText={selectedPeriod?.is_adjustment_rate_locked ?
                  "確定済み（HR評価管理画面で計算・確定）" :
                  "R計算後に自動設定されます"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={formData.status}
                  label="ステータス"
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <MenuItem value="draft">下書き</MenuItem>
                  <MenuItem value="root_defined">ルート定義済</MenuItem>
                  <MenuItem value="active">有効</MenuItem>
                  <MenuItem value="closed">終了</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedPeriod?.is_adjustment_rate_locked && (
              <Grid item xs={12}>
                <Alert severity="info">
                  この評価期間の会社調整率は既に確定されています。目標平均昇給率の変更はできません。
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>評価期間の削除</DialogTitle>
        <DialogContent>
          <Typography>
            評価期間「{periodToDelete?.period_name}」を削除してもよろしいですか？
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            この評価期間に関連する評価ルートが存在する場合、削除できません。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>キャンセル</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationPeriodManagementPage;
